/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LogsModule */

import {
    AfterViewInit,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { customTemplates, templatesConfiguration } from 'ng/modules/logs/constants/logs-signpost.constants';

import { L10nService } from '@vmw/ngx-vip';
import { IAviDataGridConfig } from 'ng/modules/data-grid';
import { getSignificanceRequestParams } from 'ng/modules/logs/utils/logs-signpost.utils';
import { addPoolIdFilterParams } from 'ng/modules/logs/utils/logs.utils';
import { FilterOperatorType, ILogsSignificanceSignpostData } from '../../../../logs.types';

import { TrafficLogsSignpostStore } from '../../../traffic-logs/traffic-logs-sidebar/traffic-logs-signpost.store';
import * as l10n from './logs-signpost-custom-significance.l10n';
import './logs-signpost-custom-significance.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Signpost component for significance section.
 * @author Suraj Kumar, Yogesh Patil
 */
@Component({
    selector: 'logs-signpost-custom-significance',
    templateUrl: './logs-signpost-custom-significance.component.html',
})
export class LogsSignpostCustomSignificanceComponent implements OnInit, AfterViewInit {
    /**
     * The ID of the pool used to filter logs from a VS.
     */
    @Input()
    public poolId?: string;

    /**
     * Datagrid template for name column.
     */
    @ViewChild('nameFieldTemplateRef')
    public readonly nameFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for column progress bar.
     */
    @ViewChild('progressBarFieldTemplateRef')
    public readonly progressBarFieldTemplateRef: TemplateRef<HTMLElement>;

    public significanceGroupsGridConfig: IAviDataGridConfig;

    public readonly FilterOperatorType = FilterOperatorType;

    public readonly l10nKeys = l10nKeys;

    constructor(
        public readonly trafficLogsSignpostStore: TrafficLogsSignpostStore,
        private readonly l10nService: L10nService,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        const fullSignpostParams = addPoolIdFilterParams(getSignificanceRequestParams(), this.poolId);

        this.trafficLogsSignpostStore.getLogSignpostData(fullSignpostParams);
    }

    /** @override */
    public ngAfterViewInit(): void {
        const { l10nService } = this;
        const configKey = customTemplates.significance;

        this.significanceGroupsGridConfig = {
            layout: {
                hidePagination: true,
                hideCheckboxes: true,
                showFooter: false,
            },
            fields: [
                {
                    label: templatesConfiguration[configKey].name,
                    id: 'name',
                    templateRef: this.nameFieldTemplateRef,
                    expandedTemplateRef: this.nameFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(
                        l10nKeys.logsNumberLabel,
                    ),
                    id: 'logs',
                    transform: (
                        row: ILogsSignificanceSignpostData,
                    ): number => row.count,
                    expandedTransform: (
                        row: ILogsSignificanceSignpostData,
                    ): number => row.count,
                },
                {
                    label: l10nService.getMessage(l10nKeys.logsPercentageLabel),
                    id: 'percentage',
                    transform: (
                        row: ILogsSignificanceSignpostData,
                    ): string => `${row.percentage}%`,
                    expandedTransform: (
                        row: ILogsSignificanceSignpostData,
                    ): string => `${row.percentage}%`,
                },
                {
                    label: '',
                    id: 'progress-bar',
                    templateRef: this.progressBarFieldTemplateRef,
                    expandedTemplateRef: this.progressBarFieldTemplateRef,
                },
            ],
            isExpandedContentColumnBased: true,
        };
    }
}
